import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Typography,
  Upload,
  Button,
  Progress,
  Table,
  Tag,
  Space,
  Alert,
  Card,
  Row,
  Col,
  Statistic,
  Tooltip,
  Popconfirm,
} from 'antd';
import {
  InboxOutlined,
  FileZipOutlined,
  EyeOutlined,
  DownloadOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  StopOutlined,
  DeleteOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import {
  getMmPresign,
  uploadMmChunks,
  processMmUpload,
  abortMmUpload,
  getMmStatus,
  getMmReports,
  deleteMmReport,
  renameMmReport,
} from '../api/client';

const { Title, Text, Paragraph } = Typography;
const { Dragger } = Upload;

const MAX_SIZE_GB    = 5;
const MAX_SIZE_BYTES = MAX_SIZE_GB * 1024 * 1024 * 1024;
const POLL_INTERVAL  = 3000;   // ms between status checks
const POLL_MAX       = 320;    // ~16 minutes before giving up (Lambda max is 15 min)

function FindingsBadges({ critical, high, medium, low }) {
  return (
    <Space size={4} wrap>
      {critical > 0 && <Tag color="red">{critical} CRITICAL</Tag>}
      {high > 0     && <Tag color="orange">{high} HIGH</Tag>}
      {medium > 0   && <Tag color="gold">{medium} MEDIUM</Tag>}
      {low > 0      && <Tag>{low} LOW</Tag>}
      {critical === 0 && high === 0 && medium === 0 && low === 0 && (
        <Tag color="green">No findings</Tag>
      )}
    </Space>
  );
}

export default function MmAnalyze() {
  const [uploading, setUploading]     = useState(false);
  const [uploadPhase, setUploadPhase] = useState(''); // 'uploading' | 'analyzing'
  const [uploadPct, setUploadPct]     = useState(0);
  const [lastResult, setLastResult]   = useState(null);
  const [lastError, setLastError]     = useState(null);
  const [reports, setReports]         = useState([]);
  const [loadingReports, setLoadingReports] = useState(true);
  const [deletingId, setDeletingId]   = useState(null);
  const [renamingId, setRenamingId]   = useState(null);

  // Stores {controller, upload_id, zip_key} for the cancel handler
  const uploadStateRef = useRef(null);
  // Stores the setInterval ID for the status polling loop
  const pollingRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const fetchReports = useCallback(async () => {
    setLoadingReports(true);
    try {
      const data = await getMmReports();
      setReports(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load reports:', err);
    } finally {
      setLoadingReports(false);
    }
  }, []);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  // Kick off polling after /process returns {report_id, status:"processing"}
  const startPolling = useCallback((report_id) => {
    let attempts = 0;

    const id = setInterval(async () => {
      attempts++;
      if (attempts > POLL_MAX) {
        stopPolling();
        setLastError('Analysis is taking longer than expected — it may still complete in the background. Refresh the table in a few minutes.');
        setUploading(false);
        setUploadPhase('');
        return;
      }

      try {
        const data = await getMmStatus(report_id);

        if (data.status === 'complete') {
          stopPolling();
          setLastResult(data);
          fetchReports();
          setUploading(false);
          setUploadPhase('');
        } else if (data.status === 'failed') {
          stopPolling();
          setLastError(data.error || 'Analysis failed — check Lambda logs for details');
          setUploading(false);
          setUploadPhase('');
        }
        // 'processing' → keep polling
      } catch (err) {
        // Non-fatal — network blip, keep retrying
        console.warn('Status poll error (retrying):', err);
      }
    }, POLL_INTERVAL);

    pollingRef.current = id;
  }, [stopPolling, fetchReports]);

  const handleUpload = async (file) => {
    if (file.size > MAX_SIZE_BYTES) {
      setLastError(`File exceeds ${MAX_SIZE_GB} GB limit.`);
      return false;
    }

    const controller = new AbortController();
    uploadStateRef.current = { controller };

    setUploading(true);
    setUploadPhase('uploading');
    setUploadPct(0);
    setLastResult(null);
    setLastError(null);

    let pollingStarted = false;

    try {
      // Step 1: create multipart upload, get part URLs
      const presignData = await getMmPresign(file.name);
      const { report_id, upload_id, zip_key } = presignData;
      uploadStateRef.current = { controller, upload_id, zip_key };

      // Step 2: upload all chunks directly to S3
      const parts = await uploadMmChunks(
        presignData,
        file,
        (pct) => setUploadPct(pct),
        controller.signal,
      );

      setUploadPct(100);
      setUploadPhase('analyzing');

      // Step 3: complete multipart + trigger async analysis (returns immediately)
      const result = await processMmUpload({ report_id, zip_key, upload_id }, parts);

      // Step 4: poll /status until complete or failed
      startPolling(result.report_id);
      pollingStarted = true;

    } catch (err) {
      if (err.name === 'AbortError' || err.message === 'Cancelled') {
        return false; // user cancelled — no error shown
      }
      const status = err?.response?.status;
      const apiMsg = err?.response?.data?.error || err?.response?.data?.message;
      const detail = apiMsg || err.message || 'Unknown error';
      setLastError(
        status
          ? `Error (HTTP ${status}): ${detail}`
          : `Error: ${detail}`
      );
    } finally {
      // Only reset upload state if polling didn't take over the 'uploading' indicator
      if (!pollingStarted) {
        uploadStateRef.current = null;
        setUploading(false);
        setUploadPhase('');
      }
    }

    return false;
  };

  const handleCancel = async () => {
    // Stop any active status polling
    stopPolling();

    const state       = uploadStateRef.current;
    const phase       = uploadPhase;

    // Abort in-flight XHR chunks (only meaningful during 'uploading' phase)
    if (state?.controller) state.controller.abort();

    // Tell S3 to discard the incomplete multipart upload
    if (phase === 'uploading' && state?.upload_id && state?.zip_key) {
      try {
        await abortMmUpload({ upload_id: state.upload_id, zip_key: state.zip_key });
      } catch (e) {
        console.warn('abort_multipart_upload failed (non-fatal):', e);
      }
    }
    // Note: if phase === 'analyzing', the async Lambda will still finish and
    // write results to S3 — we simply stop polling and ignore those results.

    uploadStateRef.current = null;
    setUploading(false);
    setUploadPhase('');
    setUploadPct(0);
    setLastError(null);
  };

  const handleDelete = async (report_id) => {
    setDeletingId(report_id);
    try {
      await deleteMmReport(report_id);
      setReports((prev) => prev.filter((r) => r.report_id !== report_id));
    } catch (err) {
      const detail = err?.response?.data?.error || err.message || 'Unknown error';
      setLastError(`Delete failed: ${detail}`);
    } finally {
      setDeletingId(null);
    }
  };

  const handleRename = async (report_id, friendly_name) => {
    const trimmed = friendly_name.trim();
    if (!trimmed) return;
    setRenamingId(report_id);
    try {
      await renameMmReport(report_id, trimmed);
      setReports((prev) =>
        prev.map((r) => r.report_id === report_id ? { ...r, friendly_name: trimmed } : r)
      );
    } catch (err) {
      const detail = err?.response?.data?.error || err.message || 'Unknown error';
      setLastError(`Rename failed: ${detail}`);
    } finally {
      setRenamingId(null);
    }
  };

  const columns = [
    {
      title: 'Name',
      key: 'name',
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Text
            editable={{
              tooltip: 'Click to set a friendly name',
              text: r.friendly_name || r.filename,
              onChange: (val) => handleRename(r.report_id, val),
              icon: renamingId === r.report_id ? <LoadingOutlined /> : undefined,
            }}
            style={{ fontWeight: 500 }}
          >
            {r.friendly_name || r.filename}
          </Text>
          {r.friendly_name && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              <FileZipOutlined style={{ marginRight: 4 }} />
              {r.filename}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Analyzed',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 190,
      render: (v) => {
        try { return new Date(v).toLocaleString(); } catch { return v; }
      },
    },
    {
      title: 'Findings',
      key: 'findings',
      render: (_, r) => (
        <FindingsBadges
          critical={r.critical}
          high={r.high}
          medium={r.medium}
          low={r.low}
        />
      ),
    },
    {
      title: 'Actions',
      key: 'action',
      width: 260,
      render: (_, r) => (
        <Space>
          <Tooltip title="Opens in a new tab (link valid for 7 days)">
            <Button
              type="primary"
              size="small"
              icon={<EyeOutlined />}
              href={r.view_url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ backgroundColor: '#E8741A', borderColor: '#E8741A' }}
            >
              View
            </Button>
          </Tooltip>
          {r.download_url && (
            <Tooltip title="Download original ZIP (link valid for 7 days)">
              <Button
                size="small"
                icon={<DownloadOutlined />}
                href={r.download_url}
              >
                ZIP
              </Button>
            </Tooltip>
          )}
          <Popconfirm
            title="Delete this report?"
            description="This permanently removes the report and ZIP from S3."
            onConfirm={() => handleDelete(r.report_id)}
            okText="Delete"
            okButtonProps={{ danger: true }}
            cancelText="Cancel"
          >
            <Button
              danger
              size="small"
              icon={<DeleteOutlined />}
              loading={deletingId === r.report_id}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Title level={4} style={{ marginBottom: 4 }}>
        Mattermost Support Packet Analysis
      </Title>
      <Paragraph type="secondary" style={{ marginBottom: 24 }}>
        Upload a Mattermost support ZIP. The analyzer checks goroutine health, log patterns,
        known bugs, and cluster state, then saves the HTML report to S3 so anyone with the
        link can view it for up to 7 days.
      </Paragraph>

      {/* ── Upload card ─────────────────────────────────────────────────────── */}
      <Card style={{ marginBottom: 24 }}>
        <Dragger
          accept=".zip"
          showUploadList={false}
          beforeUpload={handleUpload}
          customRequest={() => {}}
          disabled={uploading}
          style={{ padding: '8px 0' }}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined style={{ color: '#E8741A', fontSize: 48 }} />
          </p>
          <p className="ant-upload-text">
            Drag & drop a support packet ZIP here, or click to browse
          </p>
          <p className="ant-upload-hint" style={{ color: '#999' }}>
            Uploads directly to S3 — no size limit &nbsp;·&nbsp; Only .zip files &nbsp;·&nbsp;
            ZIP and HTML report stored in S3
          </p>
        </Dragger>

        {/* Upload / analysis progress */}
        {uploading && (
          <div style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
              <Text type="secondary">
                {uploadPhase === 'analyzing' ? (
                  <Space>
                    <LoadingOutlined />
                    Upload complete — running analysis (large packets may take up to 15 minutes)…
                  </Space>
                ) : (
                  `Uploading to S3… ${uploadPct}%`
                )}
              </Text>
              <Button
                danger
                size="small"
                icon={<StopOutlined />}
                onClick={handleCancel}
              >
                Cancel
              </Button>
            </div>

            {uploadPhase !== 'analyzing' && (
              <Progress
                percent={uploadPct}
                status="active"
                strokeColor="#E8741A"
              />
            )}

            {uploadPhase === 'analyzing' && (
              <Progress
                percent={99}
                status="active"
                strokeColor="#1677ff"
                format={() => 'Analyzing…'}
              />
            )}
          </div>
        )}

        {/* Persistent error */}
        {lastError && !uploading && (
          <Alert
            style={{ marginTop: 16 }}
            type="error"
            showIcon
            icon={<CloseCircleOutlined />}
            message="Error"
            description={lastError}
            closable
            onClose={() => setLastError(null)}
          />
        )}

        {/* Success result */}
        {lastResult && !uploading && (
          <Alert
            style={{ marginTop: 16 }}
            type={lastResult.critical > 0 ? 'error' : lastResult.high > 0 ? 'warning' : 'success'}
            icon={lastResult.critical > 0 ? <WarningOutlined /> : <CheckCircleOutlined />}
            showIcon
            message={
              <Space>
                <Text strong>{lastResult.filename}</Text>
                <Text type="secondary">analyzed successfully</Text>
              </Space>
            }
            description={
              <Space direction="vertical" size={8} style={{ marginTop: 8 }}>
                <Row gutter={24}>
                  <Col>
                    <Statistic
                      title="CRITICAL"
                      value={lastResult.critical}
                      valueStyle={{ color: lastResult.critical > 0 ? '#ff4d4f' : '#52c41a', fontSize: 20 }}
                    />
                  </Col>
                  <Col>
                    <Statistic
                      title="HIGH"
                      value={lastResult.high}
                      valueStyle={{ color: lastResult.high > 0 ? '#E8741A' : '#52c41a', fontSize: 20 }}
                    />
                  </Col>
                  <Col>
                    <Statistic title="MEDIUM" value={lastResult.medium} valueStyle={{ fontSize: 20 }} />
                  </Col>
                  <Col>
                    <Statistic title="LOW" value={lastResult.low} valueStyle={{ fontSize: 20 }} />
                  </Col>
                </Row>
                <Text type="secondary">Report saved — view it in the table below.</Text>
              </Space>
            }
          />
        )}
      </Card>

      {/* ── Past analyses ────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <Title level={5} style={{ margin: 0 }}>Past Analyses</Title>
        <Button
          icon={<ReloadOutlined />}
          size="small"
          onClick={fetchReports}
          loading={loadingReports}
        >
          Refresh
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={reports}
        rowKey="report_id"
        loading={loadingReports}
        size="small"
        pagination={{ pageSize: 20, hideOnSinglePage: true }}
        locale={{ emptyText: 'No analyses yet — upload a support packet above.' }}
      />
    </div>
  );
}
